import React from "react";
import { Link, useLocation } from "react-router-dom";
import { cn } from "../../utils/cn";

export interface NavLink {
  name: string;
  path: string;
  icon?: React.ReactNode;
}

export interface NavLinksProps {
  /**
   * Array of navigation links
   */
  links: NavLink[];
  /**
   * Whether to show mobile version
   */
  isMobile?: boolean;
  /**
   * Callback when a link is clicked (mobile only)
   */
  onLinkClick?: () => void;
  /**
   * Additional CSS classes
   */
  className?: string;
}

const NavLinks: React.FC<NavLinksProps> = ({
  links,
  isMobile = false,
  onLinkClick,
  className = "",
}) => {
  const location = useLocation();

  if (isMobile) {
    return (
      <div className={cn("space-y-1", className)}>
        {links.map((link) => {
          const isActive = location.pathname === link.path;

          return (
            <Link
              key={link.name}
              to={link.path}
              className={cn(
                "flex items-center py-2.5 px-3 min-h-[44px] text-sm font-medium rounded-lg transition-all duration-200",
                isActive
                  ? "text-[#575AD0] bg-[#575AD0]/5"
                  : "text-white/80 hover:text-white hover:bg-white/5",
              )}
              onClick={onLinkClick}
            >
              {link.icon && <span className="mr-2">{link.icon}</span>}
              {link.name}
            </Link>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={cn("hidden md:flex items-center space-x-8 h-full", className)}
    >
      {links.map((link) => {
        const isActive = location.pathname === link.path;

        return (
          <Link
            key={link.name}
            to={link.path}
            className={cn(
              "inline-flex items-center px-1 h-full text-sm font-medium relative transition-all duration-200",
              isActive ? "text-white" : "text-white/80 hover:text-white",
            )}
          >
            {link.icon && <span className="mr-2">{link.icon}</span>}
            {link.name}
            {/* Active indicator line */}
            {isActive && (
              <span
                className="absolute bottom-0 left-0 h-[2px] w-full bg-[#575AD0]"
                style={{
                  transform: "translateY(-1px)",
                }}
              />
            )}
          </Link>
        );
      })}
    </div>
  );
};

export default NavLinks;
